import React from "react";
import { Button } from "@narmi/design_system";
import { useHref, useSearchParams } from "react-router-dom-v5-compat";
import { useNotificationContext } from "cerulean";
import type { SettingHistory } from "./types";

type SendNotification = ReturnType<typeof useNotificationContext>["sendNotification"];

function HistoryRow({
  settingHistory,
  previousSettingHistory,
  setOldAndNewPreviewValues,
  highlightedId,
}: {
  settingHistory: SettingHistory;
  previousSettingHistory?: SettingHistory;
  setOldAndNewPreviewValues: (oldValue: string, newValue: string) => void;
  highlightedId: number | null;
}) {
  const [copied, setCopied] = React.useState(false);
  const [valueCopied, setValueCopied] = React.useState(false);
  const [searchParams] = useSearchParams();
  const isHighlighted = highlightedId === settingHistory.id;

  const nextParams = new URLSearchParams(searchParams);
  nextParams.set("log_id", String(settingHistory.id));
  const href = useHref({ search: `?${nextParams}` });
  const rowUrl = new URL(href, window.location.origin).toString();

  const handleCopyLink = () => {
    navigator.clipboard.writeText(rowUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleCopyValue = () => {
    navigator.clipboard.writeText(settingHistory.resulting_value).then(() => {
      setValueCopied(true);
      setTimeout(() => setValueCopied(false), 2000);
    });
  };

  return (
    <tr
      style={{
        borderTop: "1px solid rgb(var(--border-color))",
        backgroundColor: isHighlighted ? "#fffbcc" : "transparent",
      }}
      id={`log-${settingHistory.id}`}
    >
      <td style={{ width: "100px" }}>{new Date(settingHistory.timestamp).toLocaleDateString()}</td>
      <td style={{ maxWidth: "400px", wordWrap: "break-word" }}>
        {settingHistory.resulting_value}
      </td>
      <td>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.25rem",
            alignItems: "flex-start",
          }}
        >
          {previousSettingHistory && (
            <Button
              type="button"
              kind="plain"
              onClick={() => {
                setOldAndNewPreviewValues(
                  previousSettingHistory.resulting_value,
                  settingHistory.resulting_value
                );
              }}
            >
              View changes
            </Button>
          )}
          <Button type="button" kind="plain" onClick={handleCopyLink}>
            {copied ? "Copied!" : "Copy link"}
          </Button>
          <Button type="button" kind="plain" onClick={handleCopyValue}>
            {valueCopied ? "Copied!" : "Copy value"}
          </Button>
        </div>
      </td>
      <td>{settingHistory.user}</td>
      <td style={{ maxWidth: "200px", wordWrap: "break-word" }}>{settingHistory.note_value}</td>
    </tr>
  );
}

function HistoryTable({
  settingName,
  setOldAndNewPreviewValues,
  sendNotification,
  setIsHistoryDisplayed,
}: {
  settingName: string;
  setOldAndNewPreviewValues: (oldValue: string, newValue: string) => void;
  sendNotification: SendNotification;
  setIsHistoryDisplayed: (isDisplayed: boolean) => void;
}) {
  const [history, setHistory] = React.useState<SettingHistory[]>([]);
  const [highlightedId, setHighlightedId] = React.useState<number | null>(null);
  const [searchParams] = useSearchParams();
  const logId = searchParams.get("log_id");

  const fetchHistory = async () => {
    try {
      const response = await fetch(`/api/institution_settings/${settingName}/history/`);
      if (!response.ok) {
        throw new Error(`Setting history request failed with status ${response.status}`);
      }
      const data: SettingHistory[] = await response.json();
      setHistory(data);

      if (logId) {
        const logIdNum = parseInt(logId, 10);
        setHighlightedId(logIdNum);
        setTimeout(() => {
          const element = document.getElementById(`log-${logIdNum}`);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }, 100);
      }
    } catch {
      sendNotification({ type: "negative", text: "Failed to fetch setting history" });
      setIsHistoryDisplayed(false);
    }
  };

  React.useEffect(() => {
    fetchHistory();
  }, []);

  return (
    <table className="ui table" style={{ borderCollapse: "collapse" }}>
      <thead>
        <tr>
          <th>DATE</th>
          <th>VALUE</th>
          <th>{null}</th>
          <th>USER</th>
          <th>NOTE</th>
        </tr>
      </thead>
      <tbody>
        {history.map((settingHistory, idx) => (
          <HistoryRow
            key={settingHistory.timestamp}
            settingHistory={settingHistory}
            previousSettingHistory={history[idx + 1]}
            setOldAndNewPreviewValues={setOldAndNewPreviewValues}
            highlightedId={highlightedId}
          />
        ))}
      </tbody>
    </table>
  );
}

export default HistoryTable;
